import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { KnowledgeBaseRequest, KnowledgeBaseResponse } from 'apis/business/types';
import {
  cancelDataSet,
  cloneKnowledgeBase,
  createChunk,
  createDataSet,
  createKnowledgeBase,
  deleteChunk,
  deleteDataSet,
  deleteKnowledgeBase,
  getChunk,
  getChunks,
  getDataSet,
  getDataSets,
  getKnowledgeBase,
  getKnowledgeBaseParser,
  getKnowledgeBases,
  getRetrieveChunk,
  restartDataSet,
  updateChunk,
  updateDataSet,
  updateKnowledgeBase
} from './api';
import { queryClient } from 'providers/AllProviders';
import { useNavigate, useParams } from 'react-router-dom';
import { ChunkResponse, DatasetResponse, KnowledgeBaseParserResponse } from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';

export function useGetKnowledgeBases(
  searchText?: string | null,
  options?: UseQueryOptions<{ data: KnowledgeBaseResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useQuery<{ data: KnowledgeBaseResponse[]; meta: MetaApiResponse }>({
    queryKey: ['knowledgeBases', workspaceId, searchText],
    queryFn: () => getKnowledgeBases(workspaceId!, searchText),
    enabled: Boolean(workspaceId),
    ...options
  });
}

export function useGetKnowledgeBase(options?: UseQueryOptions<{ data: KnowledgeBaseResponse[] }, Error>) {
  const { workspaceId, knowledgeBaseId } = useParams<{ workspaceId: string; knowledgeBaseId: string }>();

  return useQuery<{ data: KnowledgeBaseResponse[] }>({
    queryKey: ['knowledgeBase', workspaceId, knowledgeBaseId],
    queryFn: () => getKnowledgeBase(workspaceId!, knowledgeBaseId!),
    enabled: Boolean(workspaceId && knowledgeBaseId),
    ...options
  });
}

export function useDeleteKnowledgeBase() {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useMutation({
    mutationFn: (id: string) => deleteKnowledgeBase(workspaceId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBases'],
        refetchType: 'active'
      });
    }
  });
}

export function useCreateOrUpdateKnowledgeBase() {
  const navigate = useNavigate();
  const { workspaceId, knowledgeBaseId } = useParams<{ workspaceId: string; knowledgeBaseId: string }>();

  return useMutation({
    mutationFn: (payload: KnowledgeBaseRequest) =>
      knowledgeBaseId
        ? updateKnowledgeBase(workspaceId!, knowledgeBaseId!, payload)
        : createKnowledgeBase(workspaceId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBases'],
        refetchType: 'active'
      });
      navigate(-1);
    }
  });
}

export function useCloneKnowledgeBase() {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useMutation({
    mutationFn: (knowledgeBaseId: number) => cloneKnowledgeBase(workspaceId!, knowledgeBaseId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBases'],
        refetchType: 'active'
      });
    }
  });
}

export function useGetKnowledgeBaseParser(options?: UseQueryOptions<KnowledgeBaseParserResponse | any, Error>) {
  return useQuery<KnowledgeBaseParserResponse | any>({
    queryKey: ['knowledgeBaseParser'],
    queryFn: () => getKnowledgeBaseParser(),
    ...options
  });
}

export function useGetDataSets(
  searchText: string | null,
  currentPage: number,
  options?: UseQueryOptions<{ data: DatasetResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId, knowledgeBaseId } = useParams<{ workspaceId: string; knowledgeBaseId: string }>();

  return useQuery<{ data: DatasetResponse[]; meta: MetaApiResponse }>({
    queryKey: ['datasets', workspaceId, knowledgeBaseId, searchText, currentPage],
    queryFn: () => getDataSets(workspaceId!, knowledgeBaseId!, searchText, currentPage),
    enabled: Boolean(workspaceId && knowledgeBaseId),
    ...options
  });
}

export function useGetDataSet(options?: UseQueryOptions<DatasetResponse, Error>) {
  const { workspaceId, knowledgeBaseId, datasetId } = useParams<{
    workspaceId: string;
    knowledgeBaseId: string;
    datasetId: string;
  }>();

  return useQuery<DatasetResponse>({
    queryKey: ['dataset', workspaceId, knowledgeBaseId, datasetId],
    queryFn: () => getDataSet(workspaceId!, knowledgeBaseId!, datasetId!),
    enabled: Boolean(workspaceId && knowledgeBaseId && datasetId),
    ...options
  });
}

export function useCreateOrUpdateDataSet() {
  const navigate = useNavigate();
  const { workspaceId, knowledgeBaseId, datasetId } = useParams<{
    workspaceId: string;
    knowledgeBaseId: string;
    datasetId: string;
  }>();

  return useMutation({
    mutationFn: (payload: any) =>
      datasetId
        ? updateDataSet(workspaceId!, knowledgeBaseId!, datasetId!, payload)
        : createDataSet(workspaceId!, knowledgeBaseId!, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['datasets', workspaceId, knowledgeBaseId],
        refetchType: 'all'
      });
      if (datasetId) {
        queryClient.invalidateQueries({
          queryKey: ['dataset', workspaceId, knowledgeBaseId, datasetId],
          refetchType: 'all'
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBases', workspaceId],
        refetchType: 'all'
      });
      if (data !== undefined) {
        navigate(-1);
      }
    }
  });
}

export function useUpdateDataSet() {
  const { workspaceId, knowledgeBaseId } = useParams<{
    workspaceId: string;
    knowledgeBaseId: string;
  }>();
  return useMutation({
    mutationFn: ({ datasetId, payload }: { datasetId: number; payload: any }) =>
      updateDataSet(workspaceId!, knowledgeBaseId!, String(datasetId!), payload),
    onSuccess: (data, variables) => {
      const { datasetId } = variables;
      queryClient.invalidateQueries({
        queryKey: ['datasets', workspaceId, knowledgeBaseId],
        refetchType: 'all'
      });
      if (datasetId) {
        queryClient.invalidateQueries({
          queryKey: ['dataset', workspaceId, knowledgeBaseId, datasetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useDeleteDataSet() {
  const { workspaceId, knowledgeBaseId } = useParams<{ workspaceId: string; knowledgeBaseId: string }>();

  return useMutation({
    mutationFn: (id: string) => deleteDataSet(workspaceId!, knowledgeBaseId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['datasets', workspaceId, knowledgeBaseId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBases', workspaceId],
        refetchType: 'active'
      });
    }
  });
}

export function useCancelDataSet(defaultKnowledgeBaseId?: number) {
  const { workspaceId, knowledgeBaseId } = useParams<{ workspaceId: string; knowledgeBaseId: string }>();

  return useMutation({
    mutationFn: (id: string) => cancelDataSet(workspaceId!, knowledgeBaseId! ?? defaultKnowledgeBaseId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['datasets', workspaceId, knowledgeBaseId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agentDatasets'],
        refetchType: 'active'
      });
    }
  });
}

export function useRestartDataSet(defaultKnowledgeBaseId?: number) {
  const { workspaceId, knowledgeBaseId } = useParams<{ workspaceId: string; knowledgeBaseId: string }>();

  return useMutation({
    mutationFn: (id: string) => restartDataSet(workspaceId!, knowledgeBaseId! ?? defaultKnowledgeBaseId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['datasets', workspaceId, knowledgeBaseId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agentDatasets'],
        refetchType: 'active'
      });
    }
  });
}

export function useGetRetrieveChunk(data: any, options?: UseQueryOptions<any, Error>) {
  const { workspaceId, knowledgeBaseId } = useParams<{ workspaceId: string; knowledgeBaseId: string }>();

  return useQuery<any>({
    queryKey: ['retrieveChunk', workspaceId, knowledgeBaseId, data],
    queryFn: () => getRetrieveChunk(workspaceId!, knowledgeBaseId!, data),
    enabled: Boolean(workspaceId && knowledgeBaseId && data),
    ...options
  });
}

export function useGetChunks(
  searchText: string | null,
  currentPage: number,
  options?: UseQueryOptions<{ data: ChunkResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId, knowledgeBaseId, datasetId } = useParams<{
    workspaceId: string;
    knowledgeBaseId: string;
    datasetId: string;
  }>();

  return useQuery<{ data: ChunkResponse[]; meta: MetaApiResponse }>({
    queryKey: ['chunks', workspaceId, knowledgeBaseId, datasetId, currentPage, searchText],
    queryFn: () => getChunks(workspaceId!, knowledgeBaseId!, datasetId!, currentPage, searchText),
    enabled: Boolean(workspaceId && knowledgeBaseId && datasetId),
    ...options
  });
}

export function useGetChunk(options?: UseQueryOptions<ChunkResponse, Error>) {
  const { workspaceId, knowledgeBaseId, datasetId, chunkId } = useParams<{
    workspaceId: string;
    knowledgeBaseId: string;
    datasetId: string;
    chunkId: string;
  }>();

  return useQuery<ChunkResponse>({
    queryKey: ['chunk', workspaceId, knowledgeBaseId, datasetId, chunkId],
    queryFn: () => getChunk(workspaceId!, knowledgeBaseId!, datasetId!, chunkId!),
    enabled: Boolean(workspaceId && knowledgeBaseId && datasetId && chunkId),
    ...options
  });
}

export function useCreateOrUpdateChunk() {
  const navigate = useNavigate();
  const { workspaceId, knowledgeBaseId, datasetId, chunkId } = useParams<{
    workspaceId: string;
    knowledgeBaseId: string;
    datasetId: string;
    chunkId: string;
  }>();

  return useMutation({
    mutationFn: (payload: any) =>
      chunkId
        ? updateChunk(workspaceId!, knowledgeBaseId!, datasetId!, chunkId!, payload)
        : createChunk(workspaceId!, knowledgeBaseId!, datasetId!, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['chunks', workspaceId, knowledgeBaseId, datasetId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['chunk', workspaceId, knowledgeBaseId, datasetId],
        refetchType: 'active'
      });
      if (data !== undefined) {
        navigate(-1);
      }
    }
  });
}

export function useDeleteChunk() {
  const { workspaceId, knowledgeBaseId, datasetId } = useParams<{
    workspaceId: string;
    knowledgeBaseId: string;
    datasetId: string;
  }>();

  return useMutation({
    mutationFn: (id: string) => deleteChunk(workspaceId!, knowledgeBaseId!, datasetId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['chunks'],
        refetchType: 'active'
      });
    }
  });
}
